import { InspectionModel, SendFailure } from '../../data/types';
import {
  DeliveryState,
  inspectionRecordService,
} from '../inspection/inspectionRecordService';

export interface DrainResult {
  /** Why the drain didn't run, or null when it did. */
  skipped: 'offline' | 'no-auth' | 'in-flight' | 'cooldown' | null;
  attempted: number;
  delivered: number;
  /** externalIds that failed this pass (they stay queued for the next drain). */
  failed: string[];
}

const nothingSent = (skipped: DrainResult['skipped'] = null): DrainResult => ({
  skipped,
  attempted: 0,
  delivered: 0,
  failed: [],
});

export interface SyncDrainDeps {
  /** Queued, Photos pending and Needs attention. */
  countPendingDelivery(): Promise<number>;
  /** Queued and Photos pending, waiting or not. */
  listEligibleForBackgroundSend(): Promise<InspectionModel[]>;
  /** The Inspection as stored now; the pass decides each one from this at its turn. */
  getInspection(externalId: string): Promise<InspectionModel | undefined>;
  /** The shared JSON push (inspectionPushService.push - per-id dedupe lives there). */
  push(
    externalId: string,
  ): Promise<{ failure: SendFailure | null; fatal: boolean }>;
  /**
   * Upload any outstanding photos and finish delivery when none fail (the shared
   * photoDeliveryService - one photo sender per Inspection).
   */
  sendPhotos(externalId: string): Promise<{ failures: string[] }>;
  isOnline(): boolean;
  hasAuth(): boolean;
  now(): number;
  /** Runs `wake` once after `delayMs` and returns a function that cancels it. */
  wakeAfter?(delayMs: number, wake: () => void): () => void;
}

/**
 * Global sync drain (P0 durable delivery): finds every Queued or Photos pending
 * inspection and delivers it, from anywhere in the app - previously a completed
 * inspection only re-sent while the user sat on its submit screen. Triggered on
 * startup, reconnect and app-foreground (see initSyncDrain).
 *
 * Safety properties:
 * - one drain at a time (concurrent calls join the running drain's promise);
 * - a cooldown between passes so trigger bursts don't thrash;
 * - per-inspection waits after a failure, and none for Needs attention (#11590,
 *   SY-6): the push and photo sender record each failure, and Retry sends one
 *   Inspection straight away through sendNow;
 * - after each pass, one more pass is scheduled for the earliest wait still running,
 *   so an Inspection that is waiting is sent even if no trigger fires;
 * - per-inspection continue-past-failure (one bad inspection never blocks the rest);
 * - a FATAL push outcome (426 contract mismatch / 403 permissions) aborts the pass -
 *   every subsequent push would fail identically;
 * - double-push safety comes from inspectionPushService's per-id dedupe plus the
 *   server's idempotent upsert semantics; photos and finishing delivery go through
 *   photoDeliveryService, which the Submit screen shares, so they run once per
 *   Inspection.
 */
export class SyncDrainService {
  private inFlight: Promise<DrainResult> | null = null;
  private lastFinishedAt = 0;
  private cancelScheduledPass: (() => void) | null = null;
  private listeners = new Set<() => void>();

  constructor(
    private readonly deps: SyncDrainDeps,
    private readonly cooldownMs = 15000,
  ) {}

  /** Subscribe to "a drain pass finished" (the pending badge refreshes on this). */
  onDrained(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** The pending count in the header: Queued, Photos pending and Needs attention. */
  countPending(): Promise<number> {
    return this.deps.countPendingDelivery();
  }

  drain(force = false): Promise<DrainResult> {
    if (this.inFlight) {
      return this.inFlight;
    }
    if (!this.deps.isOnline()) {
      return Promise.resolve(nothingSent('offline'));
    }
    if (!this.deps.hasAuth()) {
      return Promise.resolve(nothingSent('no-auth'));
    }
    if (!force && this.deps.now() - this.lastFinishedAt < this.cooldownMs) {
      return Promise.resolve(nothingSent('cooldown'));
    }

    this.inFlight = this.drainNow().finally(async () => {
      this.inFlight = null;
      this.lastFinishedAt = this.deps.now();
      this.notifyDrained();
      await this.scheduleNextPass();
    });
    return this.inFlight;
  }

  private async drainNow(): Promise<DrainResult> {
    const result = nothingSent();

    const at = this.deps.now();
    let pending: string[];
    try {
      pending = (await this.deps.listEligibleForBackgroundSend()).map(
        (inspection) => inspection.externalId,
      );
    } catch (error) {
      console.error(
        'syncDrain: failed to scan for pending inspections:',
        error,
      );
      return result;
    }

    for (const externalId of pending) {
      // Sending earlier ones takes time: this one may have been reopened,
      // re-submitted or changed since the scan.
      const current = await this.deps.getInspection(externalId);
      if (
        !current ||
        !inspectionRecordService.isDueForBackgroundSend(current, at)
      ) {
        continue;
      }
      if ((await this.deliver(current, result)) === 'fatal') {
        // 426/403: every remaining push would fail the same way.
        break;
      }
    }

    if (result.attempted > 0) {
      console.log(
        `syncDrain: ${result.delivered}/${result.attempted} delivered` +
          (result.failed.length
            ? ` (failed: ${result.failed.join(', ')})`
            : ''),
      );
    }
    return result;
  }

  /** Retry: forgets the Inspection's failures and sends it straight away. */
  async retry(externalId: string): Promise<DrainResult> {
    await inspectionRecordService.retry(externalId);
    return this.sendNow(externalId);
  }

  /**
   * Sends one Inspection straight away, ignoring its wait and the pause between
   * passes. It is sent once even when a pass is running, because the push and the photo
   * sender each run once at a time per Inspection.
   */
  async sendNow(externalId: string): Promise<DrainResult> {
    if (!this.deps.isOnline()) return nothingSent('offline');
    if (!this.deps.hasAuth()) return nothingSent('no-auth');
    const result = nothingSent();

    const current = await this.deps.getInspection(externalId);
    if (
      current &&
      inspectionRecordService.isEligibleForBackgroundSend(current)
    ) {
      await this.deliver(current, result);
    }
    this.notifyDrained();
    await this.scheduleNextPass();
    return result;
  }

  /**
   * The triggers (startup, reconnect, foreground) don't fire while the app stays open
   * and online, so a pass is scheduled for when the earliest wait ends.
   */
  private async scheduleNextPass(): Promise<void> {
    if (!this.deps.wakeAfter) return;
    this.cancelScheduledPass?.();
    this.cancelScheduledPass = null;

    let inspections: InspectionModel[];
    try {
      inspections = await this.deps.listEligibleForBackgroundSend();
    } catch (error) {
      console.error('syncDrain: failed to schedule the next pass:', error);
      return;
    }
    const now = this.deps.now();
    const nextAttemptAt = Math.min(
      ...inspections
        .filter(
          (inspection) =>
            !inspectionRecordService.isDueForBackgroundSend(inspection, now),
        )
        .map((inspection) => inspection.sendFailures?.nextAttemptAt ?? now),
    );
    if (!Number.isFinite(nextAttemptAt)) return;

    this.cancelScheduledPass = this.deps.wakeAfter(nextAttemptAt - now, () => {
      this.cancelScheduledPass = null;
      void this.drain(true);
    });
  }

  private notifyDrained(): void {
    this.listeners.forEach((listener) => {
      try {
        listener();
      } catch {
        /* a bad listener must not break the drain */
      }
    });
  }

  /** One attempt at one Inspection: the record, unless only photos remain, then photos. */
  private async deliver(
    inspection: InspectionModel,
    result: DrainResult,
  ): Promise<'delivered' | 'failed' | 'fatal'> {
    const externalId = inspection.externalId;
    result.attempted++;
    try {
      const pushOutcome =
        inspectionRecordService.deliveryState(inspection) ===
        DeliveryState.PhotosPending
          ? null
          : await this.deps.push(externalId);
      if (pushOutcome?.failure) {
        result.failed.push(externalId);
        return pushOutcome.fatal ? 'fatal' : 'failed';
      }

      const photoOutcome = await this.deps.sendPhotos(externalId);
      if (photoOutcome.failures.length > 0) {
        // Photos remain; the Inspection stays Photos pending for the next pass.
        result.failed.push(externalId);
        return 'failed';
      }
      result.delivered++;
      return 'delivered';
    } catch (error) {
      console.error(`syncDrain: inspection ${externalId} failed:`, error);
      result.failed.push(externalId);
      return 'failed';
    }
  }
}
